document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

let homeShowcaseObserver;

const initProductGallery = (gallery) => {
  if (gallery.dataset.pocGalleryReady === 'true') return;

  const slides = [...gallery.querySelectorAll('[data-product-gallery-slide]')];
  const thumbs = [...gallery.querySelectorAll('[data-product-gallery-thumb]')];
  const lightbox = gallery.parentElement?.querySelector('[data-product-lightbox]');
  const lightboxImage = lightbox?.querySelector('[data-product-lightbox-image]');
  const imageIndexes = slides
    .map((slide, index) => slide.dataset.mediaType === 'image' ? index : -1)
    .filter(index => index >= 0);
  let currentIndex = Math.max(0, slides.findIndex(slide => slide.classList.contains('is-active')));
  let lightboxTrigger = null;

  const setMedia = (index) => {
    if (!slides.length) return;
    currentIndex = (index + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === currentIndex;
      slide.classList.toggle('is-active', active);
      slide.hidden = !active;
      if (!active) slide.querySelectorAll('video').forEach(video => video.pause());
    });
    thumbs.forEach((thumb, thumbIndex) => {
      const active = thumbIndex === currentIndex;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', String(active));
    });
    thumbs[currentIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  };

  const updateLightbox = (index) => {
    const imagePosition = imageIndexes.indexOf(index);
    if (imagePosition < 0 || !lightboxImage) return;
    setMedia(index);
    const opener = slides[index].querySelector('[data-product-lightbox-open]');
    lightboxImage.src = opener?.dataset.imageSrc || '';
    lightboxImage.alt = opener?.dataset.imageAlt || '';
  };

  const moveLightbox = (step) => {
    if (!imageIndexes.length) return;
    const position = imageIndexes.indexOf(currentIndex);
    const nextPosition = ((position >= 0 ? position : 0) + step + imageIndexes.length) % imageIndexes.length;
    updateLightbox(imageIndexes[nextPosition]);
  };

  const closeLightbox = () => {
    if (!lightbox) return;
    if (typeof lightbox.close === 'function' && lightbox.open) lightbox.close();
    else lightbox.removeAttribute('open');
  };

  thumbs.forEach(thumb => thumb.addEventListener('click', () => setMedia(Number(thumb.dataset.mediaIndex))));
  gallery.querySelector('[data-product-gallery-previous]')?.addEventListener('click', () => setMedia(currentIndex - 1));
  gallery.querySelector('[data-product-gallery-next]')?.addEventListener('click', () => setMedia(currentIndex + 1));

  gallery.querySelectorAll('[data-product-lightbox-open]').forEach(button => {
    button.addEventListener('click', () => {
      lightboxTrigger = button;
      updateLightbox(Number(button.closest('[data-product-gallery-slide]')?.dataset.mediaIndex || 0));
      if (typeof lightbox?.showModal === 'function') lightbox.showModal();
      else lightbox?.setAttribute('open', '');
      document.body.classList.add('poc-lightbox-open');
      lightbox?.querySelector('[data-product-lightbox-close]')?.focus();
    });
  });

  lightbox?.querySelector('[data-product-lightbox-close]')?.addEventListener('click', closeLightbox);
  lightbox?.querySelector('[data-product-lightbox-previous]')?.addEventListener('click', () => moveLightbox(-1));
  lightbox?.querySelector('[data-product-lightbox-next]')?.addEventListener('click', () => moveLightbox(1));
  lightbox?.addEventListener('click', event => {
    if (event.target === lightbox) closeLightbox();
  });
  lightbox?.addEventListener('close', () => {
    document.body.classList.remove('poc-lightbox-open');
    lightboxImage?.removeAttribute('src');
    lightboxTrigger?.focus();
    lightboxTrigger = null;
  });
  lightbox?.addEventListener('cancel', () => {
    document.body.classList.remove('poc-lightbox-open');
  });

  gallery.dataset.pocGalleryReady = 'true';
};

const initProductAccordion = (accordion) => {
  if (accordion.dataset.pocAccordionReady === 'true') return;
  const buttons = [...accordion.querySelectorAll('button[aria-controls]')];
  buttons.forEach(button => button.addEventListener('click', () => {
    const shouldOpen = button.getAttribute('aria-expanded') !== 'true';
    buttons.forEach(item => {
      const open = item === button && shouldOpen;
      item.setAttribute('aria-expanded', String(open));
      document.getElementById(item.getAttribute('aria-controls'))?.setAttribute('aria-hidden', String(!open));
    });
  }));
  accordion.dataset.pocAccordionReady = 'true';
};

const initProductTitle = (title) => {
  if (title.dataset.pocTitleReady === 'true') return;
  const originalTitle = title.textContent.trim();
  const segmenter = typeof Intl.Segmenter === 'function'
    ? new Intl.Segmenter(document.documentElement.lang || 'en', { granularity: 'word' })
    : null;

  title.setAttribute('aria-label', originalTitle);
  const rebuild = () => {
    if (!segmenter) {
      title.textContent = originalTitle;
      return;
    }
    const fragment = document.createDocumentFragment();
    for (const { segment, isWordLike } of segmenter.segment(originalTitle)) {
      if (!isWordLike) {
        fragment.append(document.createTextNode(segment));
        continue;
      }
      const token = document.createElement('span');
      token.className = 'poc-product-title-token';
      token.textContent = segment;
      fragment.append(token);
    }
    title.replaceChildren(fragment);
  };

  let frame = 0;
  const fit = () => {
    rebuild();
    title.style.fontSize = '';
    const style = getComputedStyle(title);
    const minimum = parseFloat(style.getPropertyValue('--poc-product-title-min'));
    let size = parseFloat(style.fontSize);
    const maximumLines = window.innerWidth > 900 ? 3 : 4;
    title.querySelectorAll('.poc-product-title-token').forEach(token => {
      if (token.getBoundingClientRect().width > title.clientWidth) token.classList.add('poc-product-title-token--overlong');
    });
    const lineCount = () => Math.round(title.getBoundingClientRect().height / parseFloat(getComputedStyle(title).lineHeight));
    while (size > minimum && lineCount() > maximumLines) {
      size = Math.max(minimum, size - 1);
      title.style.fontSize = `${size}px`;
    }
  };

  const scheduleFit = () => {
    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      frame = 0;
      fit();
    });
  };
  window.addEventListener('resize', scheduleFit, { passive: true });
  if ('ResizeObserver' in window) {
    let parentWidth = title.parentElement.clientWidth;
    new ResizeObserver(() => {
      const nextWidth = title.parentElement.clientWidth;
      if (nextWidth !== parentWidth) {
        parentWidth = nextWidth;
        scheduleFit();
      }
    }).observe(title.parentElement);
  }
  title.dataset.pocTitleReady = 'true';
  fit();
};

const initProductDetail = (root = document) => {
  root.querySelectorAll('[data-product-gallery]').forEach(initProductGallery);
  root.querySelectorAll('[data-product-accordion]').forEach(initProductAccordion);
  root.querySelectorAll('[data-product-title]').forEach(initProductTitle);
};

const initContactProduct = (root = document) => {
  root.querySelectorAll('[data-contact-product]:not([data-contact-product-ready])').forEach(input => {
    input.dataset.contactProductReady = 'true';
    if (input.value) return;

    const handle = new URLSearchParams(window.location.search).get('product');
    if (!handle) return;

    const list = document.getElementById(input.getAttribute('list'));
    const option = [...(list?.options || [])].find(item => item.dataset.productHandle === handle);
    if (option) input.value = option.value;
  });
};

const initHomeShowcaseReveal = (root = document) => {
  const items = [...root.querySelectorAll('[data-poc-showcase-item]:not([data-poc-reveal-ready])')];
  if (!items.length) return;

  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion || !('IntersectionObserver' in window)) {
    items.forEach(item => {
      item.dataset.pocRevealReady = 'true';
      item.classList.add('is-visible');
    });
    return;
  }

  homeShowcaseObserver ||= new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.18) {
        entry.target.classList.add('is-visible');
        return;
      }

      if (!entry.isIntersecting && entry.boundingClientRect.top > (entry.rootBounds?.bottom || window.innerHeight) + 24) {
        entry.target.classList.remove('is-visible');
      }
    });
  }, { threshold: [0, 0.18], rootMargin: '0px 0px -8% 0px' });

  items.forEach(item => {
    item.dataset.pocRevealReady = 'true';
    homeShowcaseObserver.observe(item);
  });
};

initHomeShowcaseReveal();
initProductDetail();
initContactProduct();

const closeLanguageSwitcher = (switcher, restoreFocus = false) => {
  const toggle = switcher?.querySelector('[data-language-toggle]');
  const menu = switcher?.querySelector('[data-language-menu]');
  if (!toggle || !menu) return;
  switcher.classList.remove('is-open');
  toggle.setAttribute('aria-expanded', 'false');
  menu.hidden = true;
  if (restoreFocus) toggle.focus();
};

const openLanguageSwitcher = (switcher, focusIndex) => {
  const toggle = switcher?.querySelector('[data-language-toggle]');
  const menu = switcher?.querySelector('[data-language-menu]');
  if (!toggle || !menu) return;
  switcher.classList.add('is-open');
  toggle.setAttribute('aria-expanded', 'true');
  menu.hidden = false;
  if (Number.isInteger(focusIndex)) {
    menu.querySelectorAll('[role="menuitemradio"]')[focusIndex]?.focus();
  }
};

document.addEventListener('click', (event) => {
  const menuToggle = event.target.closest('[data-mobile-menu-toggle]');
  if (menuToggle) {
    const nav = document.getElementById(menuToggle.getAttribute('aria-controls'));
    const open = menuToggle.getAttribute('aria-expanded') !== 'true';
    menuToggle.setAttribute('aria-expanded', String(open));
    const label = open ? menuToggle.dataset.closeLabel : menuToggle.dataset.openLabel;
    if (label) menuToggle.setAttribute('aria-label', label);
    nav?.classList.toggle('is-open', open);
    return;
  }

  const languageToggle = event.target.closest('[data-language-toggle]');
  if (languageToggle) {
    const switcher = languageToggle.closest('[data-language-switcher]');
    const open = languageToggle.getAttribute('aria-expanded') !== 'true';
    document.querySelectorAll('[data-language-switcher].is-open').forEach(item => {
      if (item !== switcher) closeLanguageSwitcher(item);
    });
    if (open) openLanguageSwitcher(switcher);
    else closeLanguageSwitcher(switcher);
    return;
  }

  document.querySelectorAll('[data-language-switcher].is-open').forEach(switcher => {
    if (!switcher.contains(event.target)) closeLanguageSwitcher(switcher);
  });
});

document.addEventListener('keydown', (event) => {
  const switcher = event.target.closest('[data-language-switcher]');
  if (!switcher) return;
  const toggle = switcher.querySelector('[data-language-toggle]');
  const options = [...switcher.querySelectorAll('[role="menuitemradio"]')];

  if (event.key === 'Escape') {
    event.preventDefault();
    closeLanguageSwitcher(switcher, true);
    return;
  }

  if (event.target === toggle && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
    event.preventDefault();
    openLanguageSwitcher(switcher, event.key === 'ArrowDown' ? 0 : options.length - 1);
    return;
  }

  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || !options.includes(event.target)) return;
  event.preventDefault();
  const current = options.indexOf(event.target);
  const next = event.key === 'Home' ? 0
    : event.key === 'End' ? options.length - 1
    : event.key === 'ArrowDown' ? (current + 1) % options.length
    : (current - 1 + options.length) % options.length;
  options[next]?.focus();
});

document.addEventListener('shopify:section:load', (event) => {
  document.querySelectorAll('[data-language-menu]').forEach(menu => { menu.hidden = true; });
  initHomeShowcaseReveal(event.target);
  initProductDetail(event.target);
  initContactProduct(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  event.target.closest('[data-poc-showcase-item]')?.classList.add('is-visible');
});
